// ============================================================================
// MILESTONE ROADMAP
//
// One column per delivery phase, plus an Unscheduled column, across every
// session in the project. This is the view that answers "what ships in Phase
// 1" without joining anything by hand.
//
// Multi-select works in every column, not just Unscheduled: moving work
// between phases is at least as common as scheduling it for the first time,
// and the bulk assign endpoint does not care where an item came from.
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  Milestone as MilestoneIcon, Package, BookOpen, CalendarDays, Loader2, CheckSquare, Square, X,
} from 'lucide-react';
import { useQueries } from '@tanstack/react-query';
import { useMilestones, useMilestoneMutations } from '../../hooks/useMilestones';
import { useProjectRequirements } from '../../hooks/useProjectRequirements';
import { useProjectSessions } from '../../hooks/useProjects';
import { getFeatures } from '../../services/workshop';
import { Milestone } from '../../types/raid';
import { toast } from 'sonner';

interface RoadmapItem {
  kind: 'story' | 'feature';
  id: string;
  sessionId?: string;
  label: string;
  sublabel?: string;
  milestoneId?: string;
}

const UNSCHEDULED = '__unscheduled__';

export function MilestoneRoadmap({ projectId, token }: Readonly<{ projectId: string; token: string }>) {
  const { data: milestones = [] } = useMilestones(projectId);
  const { data: requirements, isLoading: storiesLoading } = useProjectRequirements(projectId, token);
  const { data: sessions = [] } = useProjectSessions(projectId);
  const { assign } = useMilestoneMutations(projectId);

  // Features are session-scoped with no project-level aggregate, so gather them
  // the same way the requirements hook gathers stories.
  const featureQueries = useQueries({
    queries: sessions.map((s: { sessionId: string }) => ({
      queryKey: ['features', s.sessionId],
      queryFn: () => getFeatures(s.sessionId),
      staleTime: 60000,
    })),
  });
  const featuresLoading = featureQueries.some(q => q.isLoading);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const items = useMemo<RoadmapItem[]>(() => {
    const out: RoadmapItem[] = [];
    for (const s of requirements?.userStories ?? []) {
      out.push({
        kind: 'story',
        id: s.storyId,
        sessionId: s.sessionId,
        label: s.story,
        sublabel: s.persona,
        milestoneId: s.milestoneId,
      });
    }
    featureQueries.forEach((q, i) => {
      for (const f of q.data ?? []) {
        out.push({
          kind: 'feature',
          id: f.id,
          sessionId: sessions[i]?.sessionId,
          label: f.name,
          sublabel: f.description,
          milestoneId: (f as any).milestoneId,
        });
      }
    });
    return out;
  }, [requirements, featureQueries, sessions]);

  const columns = useMemo(() => {
    const byMilestone = new Map<string, RoadmapItem[]>();
    byMilestone.set(UNSCHEDULED, []);
    for (const m of milestones) byMilestone.set(m.milestoneId, []);
    for (const item of items) {
      // An item pointing at a deleted phase falls back to Unscheduled rather
      // than vanishing from the board.
      const key = item.milestoneId && byMilestone.has(item.milestoneId) ? item.milestoneId : UNSCHEDULED;
      byMilestone.get(key)!.push(item);
    }
    return byMilestone;
  }, [items, milestones]);

  const key = (i: RoadmapItem) => `${i.kind}:${i.sessionId ?? ''}:${i.id}`;

  const toggle = (i: RoadmapItem) => setSelected(prev => {
    const next = new Set(prev);
    const k = key(i);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const selectedItems = items.filter(i => selected.has(key(i)));

  const assignSelected = async (milestoneId: string) => {
    // The bulk endpoint is session-scoped for features and session stories, so
    // one call per session rather than one call overall.
    const bySession = new Map<string, RoadmapItem[]>();
    for (const i of selectedItems) {
      const sid = i.sessionId ?? '';
      bySession.set(sid, [...(bySession.get(sid) ?? []), i]);
    }

    let assigned = 0;
    const problems: string[] = [];
    for (const [sessionId, group] of Array.from(bySession)) {
      try {
        const res = await assign.mutateAsync({
          milestoneId,
          sessionId: sessionId || undefined,
          storyIds: group.filter(g => g.kind === 'story').map(g => g.id),
          featureIds: group.filter(g => g.kind === 'feature').map(g => g.id),
        });
        assigned += res.assigned;
        problems.push(...res.failures.map(f => f.id));
      } catch {
        problems.push(...group.map(g => g.id));
      }
    }

    if (problems.length > 0) toast.warning(`Moved ${assigned}, ${problems.length} failed`);
    else toast.success(`Moved ${assigned} item${assigned === 1 ? '' : 's'}`);
    setSelected(new Set());
  };

  if (milestones.length === 0) {
    return (
      <div className="rounded-xl bg-white p-12 border border-slate-200 text-center">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <MilestoneIcon size={28} className="text-slate-400" />
        </div>
        <p className="text-slate-500 text-sm">No phases to lay out yet.</p>
        <p className="text-slate-400 text-xs mt-2">Add a milestone in the list view first.</p>
      </div>
    );
  }

  const loading = storiesLoading || featuresLoading;

  return (
    <div className="flex flex-col gap-4">
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/30 bg-primary/5">
          <span className="text-sm font-semibold text-navy">
            {selected.size} selected
          </span>
          <select
            aria-label="Move selected to phase"
            defaultValue=""
            onChange={e => { if (e.target.value) assignSelected(e.target.value); }}
            className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy bg-white outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="" disabled>Move to phase…</option>
            {milestones.map(m => <option key={m.milestoneId} value={m.milestoneId}>{m.name}</option>)}
          </select>
          {assign.isPending && <Loader2 size={15} className="animate-spin text-primary" />}
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
          >
            <X size={12} /> Clear selection
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-16 text-[#64748b] text-sm">Loading roadmap…</div>
      )}

      {!loading && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {milestones.map(m => (
            <Column
              key={m.milestoneId}
              title={m.name}
              subtitle={m.targetDate}
              status={m.status}
              items={columns.get(m.milestoneId) ?? []}
              selected={selected}
              itemKey={key}
              onToggle={toggle}
            />
          ))}
          <Column
            title="Unscheduled"
            items={columns.get(UNSCHEDULED) ?? []}
            selected={selected}
            itemKey={key}
            onToggle={toggle}
            muted
          />
        </div>
      )}
    </div>
  );
}

interface ColumnProps {
  title: string;
  subtitle?: string;
  status?: Milestone['status'];
  items: RoadmapItem[];
  selected: Set<string>;
  itemKey: (i: RoadmapItem) => string;
  onToggle: (i: RoadmapItem) => void;
  muted?: boolean;
}

function Column({ title, subtitle, status, items, selected, itemKey, onToggle, muted }: Readonly<ColumnProps>) {
  const features = items.filter(i => i.kind === 'feature');
  const stories = items.filter(i => i.kind === 'story');

  return (
    <div className={`flex flex-col w-[300px] shrink-0 rounded-2xl border ${muted ? 'border-dashed border-slate-300 bg-slate-50/50' : 'border-[#e2e8f0] bg-white'}`}>
      <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center gap-2">
        <MilestoneIcon size={14} className={muted ? 'text-slate-400' : 'text-primary'} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-navy truncate">{title}</p>
          {subtitle && (
            <p className="flex items-center gap-1 text-[10px] text-slate-400">
              <CalendarDays size={9} />{subtitle}
            </p>
          )}
        </div>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">
          {items.length}
        </span>
      </div>

      <div className="flex-1 p-3 flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
        {items.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-6">
            {muted ? 'Everything is scheduled.' : 'Nothing in this phase yet.'}
          </p>
        )}

        {features.map(f => (
          <ItemCard key={itemKey(f)} item={f} isSelected={selected.has(itemKey(f))} onToggle={onToggle} />
        ))}
        {features.length > 0 && stories.length > 0 && <div className="h-px bg-slate-200 my-1" />}
        {stories.map(s => (
          <ItemCard key={itemKey(s)} item={s} isSelected={selected.has(itemKey(s))} onToggle={onToggle} />
        ))}
      </div>

      {status && status !== 'planned' && (
        <div className="px-4 py-2 border-t border-[#e2e8f0] text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          {status.replace('_', ' ')}
        </div>
      )}
    </div>
  );
}

function ItemCard({ item, isSelected, onToggle }: Readonly<{ item: RoadmapItem; isSelected: boolean; onToggle: (i: RoadmapItem) => void }>) {
  const Icon = item.kind === 'feature' ? Package : BookOpen;
  return (
    <button
      type="button"
      onClick={() => onToggle(item)}
      className={`w-full text-left flex items-start gap-2 p-2 rounded-xl border transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'border-[#e2e8f0] bg-white hover:border-primary/40'
      }`}
    >
      {isSelected
        ? <CheckSquare size={13} className="text-primary shrink-0 mt-0.5" />
        : <Square size={13} className="text-slate-300 shrink-0 mt-0.5" />}
      <Icon size={12} className={`shrink-0 mt-0.5 ${item.kind === 'feature' ? 'text-violet-500' : 'text-slate-400'}`} />
      <span className="min-w-0">
        <span className="block text-xs text-slate-700 line-clamp-2">{item.label}</span>
        {item.sublabel && <span className="block text-[10px] text-slate-400 truncate">{item.sublabel}</span>}
      </span>
    </button>
  );
}
